import type { TourStep } from "./types";
import { shellSteps } from "./steps/shell";
import { suggestionsSteps } from "./steps/suggestions";
import { overviewSteps } from "./steps/overview";
import { newSuggestionSteps } from "./steps/newSuggestion";
import { suggestionDetailSteps } from "./steps/suggestionDetail";
import { closingSteps } from "./steps/closing";

/**
 * One page of the tour. `route` is where the leg has to run; null means
 * "wherever the previous leg left us" (the closing card).
 */
export interface TourLeg {
  key: string;
  route: string | null;
  steps: TourStep[];
}

export interface BuildTourLegsOptions {
  /** False for an employee-only user, who cannot open /overview. */
  includeOverview?: boolean;
}

/**
 * Assembles the whole multi-page tour. Pure — the DOM-presence filter and
 * driver.js live in ./index, so the running order is testable on its own.
 *
 * `detailTargetId` is the suggestion the detail leg opens; when it is null
 * (the user can see no suggestions) that leg is dropped and the closing
 * copy says so. `includeOverview: false` drops the overview leg up front
 * rather than letting it time out on the route guard's redirect.
 */
export function buildTourLegs(
  detailTargetId: string | null,
  { includeOverview = true }: BuildTourLegsOptions = {},
): TourLeg[] {
  const legs: TourLeg[] = [
    {
      // Orientation rides on /suggestions rather than getting a page of
      // its own: the sidebar and account card are there, and the register
      // is where a user naturally starts.
      key: "orientation",
      route: "/suggestions",
      steps: [...shellSteps, ...suggestionsSteps],
    },
    ...(includeOverview
      ? [{ key: "overview", route: "/overview", steps: overviewSteps }]
      : []),
    {
      key: "newSuggestion",
      route: "/new-suggestion",
      steps: newSuggestionSteps,
    },
  ];

  if (detailTargetId) {
    legs.push({
      key: "suggestionDetail",
      route: `/suggestions/${detailTargetId}`,
      steps: suggestionDetailSteps,
    });
  }

  legs.push({
    key: "closing",
    route: null,
    steps: closingSteps(detailTargetId !== null),
  });

  return legs;
}
